import { useState, useCallback } from 'react';
import { AppUser, UserRole, UserStatus, UserCsvRowProcessResult } from '@/types';
import { parseCSV, downloadUserResultsCSV } from '@/utils/csvUtils';
import { saveUserApi } from '../services/adminUserApiClient';

export const useUserCsvProcessor = (existingUsers: AppUser[] = [], _appUser: AppUser | null) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingTitle, setProcessingTitle] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [processingErrorMsg, setProcessingErrorMsg] = useState<string | null>(null);
  const [generalMessage, setGeneralMessage] = useState<string | null>(null);
  const [results, setResults] = useState<UserCsvRowProcessResult[]>([]);
  const [originalFileName, setOriginalFileName] = useState('');

  const resetProcessingState = () => {
    setIsProcessing(false);
    setProcessingTitle('');
    setProcessedCount(0);
    setTotalToProcess(0);
    setIsComplete(false);
    setProcessingErrorMsg(null);
    setOriginalFileName('');
    setResults([]);
  };

  const processUserCsv = useCallback(async (file: File) => {
    if (!file) {
      setGeneralMessage('No se seleccionó ningún archivo CSV de usuarios.');
      return [];
    }

    resetProcessingState();
    setIsProcessing(true);
    setProcessingTitle(`Procesando CSV de Usuarios: ${file.name}`);
    setOriginalFileName(file.name);
    setGeneralMessage(null);

    const tempResults: UserCsvRowProcessResult[] = [];

    try {
      const csvText = await file.text();
      const { headers, rows } = parseCSV(csvText);
      setTotalToProcess(rows.length);

      const emailIndex = headers.indexOf('email');
      const displayNameIndex = headers.indexOf('displayname');
      const usernameIndex = headers.indexOf('username');
      const roleIndex = headers.indexOf('role');
      const statusIndex = headers.indexOf('status');

      if (emailIndex === -1) {
        throw new Error("El archivo CSV debe contener la columna 'email'.");
      }

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const originalRowNumber = i + 2;
        const email = row[emailIndex]?.trim().toLowerCase();

        const result: UserCsvRowProcessResult = {
          originalRowNumber,
          email: email || '',
          status: 'Fallido',
          details: '',
        };

        if (!email) {
          result.details = 'Email vacío en CSV.';
          tempResults.push(result);
          setProcessedCount((prev) => prev + 1);
          continue;
        }

        try {
          const existingUser = existingUsers.find((u) => u.email?.toLowerCase() === email);
          const userDataToSave: Partial<AppUser> = { email };

          if (displayNameIndex !== -1 && row[displayNameIndex]?.trim()) {
            userDataToSave.displayName = row[displayNameIndex].trim();
          }
          if (usernameIndex !== -1 && row[usernameIndex]?.trim()) {
            userDataToSave.username = row[usernameIndex].trim();
          }
          if (roleIndex !== -1 && row[roleIndex]?.trim()) {
            const roleVal = row[roleIndex].trim().toUpperCase() as UserRole;
            if (Object.values(UserRole).includes(roleVal)) {
              userDataToSave.role = roleVal;
            }
          }
          if (statusIndex !== -1 && row[statusIndex]?.trim()) {
            const statusStr = row[statusIndex].trim();
            const statusVal = (statusStr.charAt(0).toUpperCase() + statusStr.slice(1).toLowerCase()) as UserStatus;
            if (Object.values(UserStatus).includes(statusVal)) {
              userDataToSave.status = statusVal;
            }
          }

          if (existingUser) {
            await saveUserApi({ ...existingUser, ...userDataToSave });
            result.status = 'Éxito';
            result.details = `Usuario ${email} actualizado.`;
            result.firestoreId = existingUser.id || existingUser.uid;
          } else {
            const newUid = `usr_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
            await saveUserApi({
              id: newUid,
              uid: newUid,
              email,
              displayName: userDataToSave.displayName || email.split('@')[0],
              username: userDataToSave.username || email.split('@')[0],
              role: userDataToSave.role || UserRole.SIN_ROL,
              status: userDataToSave.status || UserStatus.PENDING,
              photoURL: null,
              onboardingCompleted: true,
            });
            result.status = 'Éxito';
            result.details = 'Usuario creado en el sistema.';
            result.firestoreId = newUid;
          }
        } catch (dbError: any) {
          result.details = `Error procesando ${email}: ${dbError?.message || dbError}`;
          result.status = 'Fallido';
        }

        tempResults.push(result);
        setProcessedCount((prev) => prev + 1);
      }

      setGeneralMessage(`Procesamiento CSV finalizado: ${tempResults.filter((r) => r.status === 'Éxito').length} procesados.`);
    } catch (error: any) {
      setProcessingErrorMsg(error?.message || 'Error al procesar el archivo');
      setGeneralMessage(error?.message || 'Error al procesar el archivo');
    } finally {
      setIsProcessing(false);
      setIsComplete(true);
      setResults(tempResults);
    }
    return tempResults;
  }, [existingUsers]);

  const downloadUserCsvResults = useCallback(() => {
    if (results.length > 0 && originalFileName) {
      downloadUserResultsCSV(results, originalFileName);
    }
  }, [results, originalFileName]);

  return {
    isProcessing,
    processingTitle,
    processedCount,
    totalToProcess,
    isComplete,
    processingErrorMsg,
    generalMessage,
    results,
    processUserCsv,
    resetProcessingState,
    setGeneralMessage,
    downloadUserCsvResults,
  };
};
